import { CASE_TYPE_MAP } from './enum';
import Constants from '../Constants';
import { EASY_MODE_STEPS } from '../constants/easyModeSteps';
import { EASY_MODE_UPLOAD_STATUS } from '../constants/easyModeDocuments';

export const PARTY_COUNT_MIN = 1;
export const PARTY_COUNT_MAX = 10;
export const ADDITIONAL_CONTEXT_MAX_LENGTH = 5000;
export const PAIN_SUFFERING_RATE_MAX = 1000000;

const IN_FLIGHT_UPLOAD_STATUSES = [
  EASY_MODE_UPLOAD_STATUS.PENDING,
  EASY_MODE_UPLOAD_STATUS.UPLOADING
];

// uploaded but no record yet, or a record still classifying: the document has not settled, so its
// parties are not readable yet and continuing would run the demand against a partial set
const PROCESSING_STATUSES = [
  EASY_MODE_UPLOAD_STATUS.UPLOADED,
  EASY_MODE_UPLOAD_STATUS.CLASSIFYING
];

// local yyyy-mm-dd, so a date-only comparison never crosses a timezone boundary
export const getTodayAsDateInput = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');

  return `${now.getFullYear()}-${month}-${day}`;
};

export const validateIncidentDate = ({ dateOfIncident }) => {
  if (!dateOfIncident) {
    return { isValid: false, error: 'Date of incident is required' };
  }

  if (Number.isNaN(new Date(dateOfIncident).getTime())) {
    return { isValid: false, error: 'Enter a valid date' };
  }

  // today is rejected as well as the future, because the gap report blocks the demand on it - letting
  // it save here only moves the bad news to the far side of a full agent run
  if (dateOfIncident >= getTodayAsDateInput()) {
    return { isValid: false, error: 'Date of incident must be before today' };
  }

  return { isValid: true, error: '' };
};

export const validatePartyCount = ({
  count,
  fieldName,
  min = PARTY_COUNT_MIN,
  max = PARTY_COUNT_MAX
}) => {
  if (!Number.isInteger(count)) {
    return { isValid: false, error: `${fieldName} must be a whole number` };
  }

  if (count < min || count > max) {
    return { isValid: false, error: `${fieldName} must be between ${min} and ${max}` };
  }

  return { isValid: true, error: '' };
};

const DEFAULT_CASE_TYPE_OPTIONS = Object.values(CASE_TYPE_MAP);

export const validateCaseType = ({ caseType, caseTypeOptions = DEFAULT_CASE_TYPE_OPTIONS }) => {
  if (!caseType) {
    return { isValid: false, error: 'Case type is required' };
  }

  if (!caseTypeOptions.some((option) => option.value === caseType)) {
    return { isValid: false, error: 'Select a supported case type' };
  }

  return { isValid: true, error: '' };
};

export const validateStateOfIncident = ({ stateOfIncident }) => {
  if (!stateOfIncident) {
    return { isValid: false, error: 'State the incident occurred in is required' };
  }

  const isKnownState = Constants.Dropdowns.States.some((state) => state.value === stateOfIncident);

  if (!isKnownState) {
    return { isValid: false, error: 'Select a state from the list' };
  }

  return { isValid: true, error: '' };
};

// The demand multiplies these out per client, so a zero prints a $0 non-economic damages claim
// rather than failing anywhere a reader would notice.
export const validatePainSufferingRate = ({ amount, fieldName }) => {
  const hasAmount = amount !== '' && amount !== null && amount !== undefined;

  if (!hasAmount) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  const rate = Number(amount);

  if (!Number.isFinite(rate)) {
    return { isValid: false, error: `${fieldName} must be a number` };
  }

  if (rate <= 0) {
    return { isValid: false, error: `${fieldName} must be more than zero` };
  }

  if (rate > PAIN_SUFFERING_RATE_MAX) {
    const formattedMax = PAIN_SUFFERING_RATE_MAX.toLocaleString('en-US');

    return { isValid: false, error: `${fieldName} must be at most $${formattedMax}` };
  }

  return { isValid: true, error: '' };
};

const collectErrors = (fieldResults) => Object.entries(fieldResults).reduce(
  (accumulator, [field, result]) => {
    if (!result.isValid) {
      accumulator[field] = result.error;
    }

    return accumulator;
  },
  {}
);

// The facts page 1 saves. Kept apart from the valuation rates because resolveEasyModeStep judges how
// far a case progressed by these alone - a case saved before the rates existed still resumes where its
// documents left off.
export const validateEasyModeCaseFacts = ({ caseInfo = {}, plaintiffCount, defendantCount, caseTypeOptions }) =>
  collectErrors({
    caseType: validateCaseType({ caseType: caseInfo.caseType, caseTypeOptions }),
    stateOfIncident: validateStateOfIncident({ stateOfIncident: caseInfo.stateOfIncident }),
    dateOfIncident: validateIncidentDate({ dateOfIncident: caseInfo.dateOfIncident }),
    plaintiffCount: validatePartyCount({ count: plaintiffCount, fieldName: 'Plaintiffs' }),
    defendantCount: validatePartyCount({ count: defendantCount, fieldName: 'Defendants' })
  });

export const validateEasyModePainSuffering = ({ painAndSuffering = {} }) => collectErrors({
  monthlyamount: validatePainSufferingRate({
    amount: painAndSuffering.monthlyamount,
    fieldName: 'Past pain and suffering per month'
  }),
  annualamount: validatePainSufferingRate({
    amount: painAndSuffering.annualamount,
    fieldName: 'Future pain and suffering per year'
  })
});

export const validateEasyModeIntake = ({
  caseInfo,
  plaintiffCount,
  defendantCount,
  caseTypeOptions
}) => {
  const errors = validateEasyModeCaseFacts({ caseInfo, plaintiffCount, defendantCount, caseTypeOptions });

  return { isValid: Object.keys(errors).length === 0, errors };
};

export const validateAdditionalContext = ({ additionalContext = '' }) => {
  if (additionalContext.length > ADDITIONAL_CONTEXT_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Keep this under ${ADDITIONAL_CONTEXT_MAX_LENGTH} characters`
    };
  }

  return { isValid: true, error: '' };
};

export const validateEasyModeDocuments = ({ rows = [] }) => {
  if (rows.length === 0) {
    return { isValid: false, error: 'Upload at least one document to continue' };
  }

  const hasUploadInFlight = rows.some((row) => IN_FLIGHT_UPLOAD_STATUSES.includes(row.status));

  if (hasUploadInFlight) {
    return { isValid: false, error: 'Wait for every upload to finish' };
  }

  const hasDocumentProcessing = rows.some((row) => PROCESSING_STATUSES.includes(row.status));

  if (hasDocumentProcessing) {
    return { isValid: false, error: 'Wait for every document to finish reading before continuing' };
  }

  const hasFailedUpload = rows.some((row) => row.status === EASY_MODE_UPLOAD_STATUS.FAILED);

  if (hasFailedUpload) {
    return { isValid: false, error: 'Retry or remove the documents that failed to upload' };
  }

  // no retry offered: the file uploaded fine and the pipeline could read nothing in it, so the same
  // bytes would only fail the same way
  const hasUnreadableDocument = rows.some((row) => row.status === EASY_MODE_UPLOAD_STATUS.BAD_RECORD);

  if (hasUnreadableDocument) {
    return { isValid: false, error: 'Remove the documents that could not be read, then upload a readable copy' };
  }

  return { isValid: true, error: '' };
};

// A document still uploading or classifying blocks the step: an errored file must surface and the run
// must never start against a partial set, so the wizard holds until every document has settled.
export const getEasyModeStepValidation = ({ intakeValues, documentRows, additionalContext, caseTypeOptions }) => ({
  [EASY_MODE_STEPS.CASE_INTAKE]: validateEasyModeIntake({ ...intakeValues, caseTypeOptions }),
  [EASY_MODE_STEPS.DOCUMENT_UPLOAD]: validateEasyModeDocuments({ rows: documentRows }),
  [EASY_MODE_STEPS.DEMAND_GENERATION]: validateAdditionalContext({ additionalContext })
});

// A saved count is what says step 1 was completed. The party arrays cannot answer that: clients[] holds
// one blank client from the moment the case is created, so their lengths read as a finished step 1 on a
// case nobody has filled in.
export const resolveEasyModeStep = ({ caseData, recordCount = 0, requestedStep, caseTypeOptions }) => {
  const step = Number(requestedStep);

  if (!Number.isInteger(step) || step < EASY_MODE_STEPS.CASE_INTAKE) {
    return EASY_MODE_STEPS.CASE_INTAKE;
  }

  const caseFactErrors = validateEasyModeCaseFacts({
    caseInfo: caseData?.caseInfo,
    plaintiffCount: caseData?.caseInfo?.plaintiffCount,
    defendantCount: caseData?.caseInfo?.defendantCount,
    caseTypeOptions
  });

  if (Object.keys(caseFactErrors).length > 0) {
    return EASY_MODE_STEPS.CASE_INTAKE;
  }

  if (recordCount === 0) {
    return Math.min(step, EASY_MODE_STEPS.DOCUMENT_UPLOAD);
  }

  return Math.min(step, EASY_MODE_STEPS.DEMAND_GENERATION);
};
